/**
 * The sidebar's seed folders — "Offers/Network Rules", "Landers" and
 * "Flows/Paths" — one entry per tracker whose banks can be pulled here
 * (enabled ∩ bankPull, registry order): Voluum and Binom always, Tres Commas
 * once it is configured and its pull is switched on.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import { Database } from 'lucide-react';
import { trackerLabel, type TrackerId } from '@trafficpulse/shared';

import { useBankPullTrackers } from '@/features/trackers/bank-pull';

export interface SeedNavItem {
  label: string;
  href: string;
  icon: typeof Database;
  permission: string;
}

export type SeedFolder = 'offers' | 'landers' | 'paths';

const FOLDERS: Record<SeedFolder, { base: string; permission: string }> = {
  offers: { base: '/admin/offers', permission: 'offer_banks.manage' },
  landers: { base: '/admin/landers', permission: 'lander_banks.manage' },
  paths: { base: '/admin/paths', permission: 'path_banks.manage' },
};

export function seedFolderItems(folder: SeedFolder, trackers: readonly TrackerId[]): SeedNavItem[] {
  const { base, permission } = FOLDERS[folder];
  return trackers.map((id) => ({
    label: trackerLabel(id),
    href: `${base}/${id}`,
    icon: Database,
    permission,
  }));
}

export function useSeedFolderItems(): Record<SeedFolder, SeedNavItem[]> {
  const trackers = useBankPullTrackers();
  return useMemo(
    () => ({
      offers: seedFolderItems('offers', trackers),
      landers: seedFolderItems('landers', trackers),
      paths: seedFolderItems('paths', trackers),
    }),
    [trackers],
  );
}
